/**
 * Hook for currency helper functions
 * Extracted from Activity.tsx to improve code organization
 */

import { useMemo } from "react";
import { Currency } from "../../../types";

export interface UseCurrencyHelpersResult {
  getCurrencyRate: (currencyCode: string) => number;
  calculateTransferRate: (fromCurrency: string, toCurrency: string) => number;
}

/**
 * Hook for currency-related helper functions
 */
export const useCurrencyHelpers = (
  currencies: Currency[],
  defaultCurrency: string
): UseCurrencyHelpersResult => {
  const getCurrencyRate = useMemo(() => {
    return (currencyCode: string): number => {
      if (currencyCode === defaultCurrency) return 1;
      const currency = currencies.find((c) => c.code === currencyCode);
      return currency?.rate || 1;
    };
  }, [currencies, defaultCurrency]);

  const calculateTransferRate = useMemo(() => {
    return (fromCurrency: string, toCurrency: string): number => {
      if (fromCurrency === toCurrency) return 1;
      const fromRate = getCurrencyRate(fromCurrency); // 1 defaultCurrency = fromRate fromCurrency
      const toRate = getCurrencyRate(toCurrency); // 1 defaultCurrency = toRate toCurrency
      
      // To convert fromCurrency to toCurrency:
      // 1. Convert to default: amount / fromRate (e.g., 380 AMD / 380 = 1 USD)
      // 2. Convert from default to toCurrency: (amount / fromRate) * toRate
      // So: transferRate = toRate / fromRate
      // Example: 1 AMD to EUR: (1 / 380) * 0.85 = 0.85 / 380 = 0.00224 (1 AMD = 0.00224 EUR)
      return toRate / fromRate; // Rate to convert from fromCurrency to toCurrency
    };
  }, [getCurrencyRate]);

  return {
    getCurrencyRate,
    calculateTransferRate,
  };
};
